import { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Package, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { paymentsAPI } from '@/lib/api';

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 45000;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const orderId = searchParams.get('order_id');

  // 'polling' | 'paid' | 'unpaid' | 'failed' | 'error'
  const [state, setState] = useState('polling');
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (authLoading || !user || !orderId) return;

    let cancelled = false;
    const startedAt = Date.now();

    const tick = async () => {
      try {
        const res = await paymentsAPI.paystackStatus(orderId);
        const ps = res.data?.payment_status;
        if (cancelled) return;
        if (ps === 'paid') {
          setState('paid');
          return;
        }
        if (ps === 'failed') {
          setState('failed');
          return;
        }
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          setState('unpaid');
          return;
        }
        timeoutRef.current = setTimeout(tick, POLL_INTERVAL_MS);
      } catch {
        if (!cancelled) setState('error');
      }
    };

    tick();
    return () => {
      cancelled = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [orderId, user, authLoading]);

  return (
    <div data-testid="payment-success-page" className="max-w-lg mx-auto px-4 py-24 text-center min-h-[70vh]">
      {state === 'polling' && (
        <>
          <div className="w-20 h-20 rounded-full bg-teal/15 flex items-center justify-center mx-auto mb-8">
            <Loader2 className="h-10 w-10 text-teal animate-spin" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-light text-foreground tracking-tight mb-4">
            Confirming your payment…
          </h1>
          <p className="font-body text-sm text-foreground/60">
            This usually takes a few seconds. Please don't close the page.
          </p>
        </>
      )}

      {state === 'paid' && (
        <>
          <div data-testid="payment-paid-state" className="w-24 h-24 rounded-full bg-teal/15 flex items-center justify-center mx-auto mb-8 animate-fade-up">
            <CheckCircle className="h-12 w-12 text-teal" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light text-foreground tracking-tight mb-4 animate-fade-up stagger-1">
            Payment Successful!
          </h1>
          <p className="font-body text-base text-foreground/70 mb-2 animate-fade-up stagger-2">
            Thank you for your order — your payment has been received.
          </p>
          {orderId && (
            <p className="font-body text-sm text-teal mb-8 animate-fade-up stagger-2">
              Order reference: <span className="font-semibold">#{orderId.slice(0, 8)}</span>
            </p>
          )}
          <div className="bg-teal/5 rounded-2xl p-5 border border-teal/20 mb-8 animate-fade-up stagger-2">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Package className="h-5 w-5 text-teal" />
              <p className="font-body text-sm font-semibold text-foreground">Collection Only</p>
            </div>
            <p className="font-body text-sm text-foreground/60">
              You'll receive an email when your order is ready for collection.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up stagger-3">
            <Link to="/profile">
              <Button variant="ctaPrimary" className="rounded-full px-8 py-6 font-body text-sm font-semibold">
                View My Orders <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/shop">
              <Button variant="ctaSecondary" className="rounded-full px-8 py-6 font-body text-sm">
                Continue Shopping
              </Button>
            </Link>
          </div>
        </>
      )}

      {(state === 'unpaid' || state === 'failed' || state === 'error') && (
        <>
          <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-8">
            <AlertTriangle className="h-10 w-10 text-destructive" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-light text-foreground tracking-tight mb-4">
            {state === 'failed' ? 'Payment Did Not Complete' : "We're Still Waiting for Confirmation"}
          </h1>
          <p className="font-body text-sm text-foreground/60 mb-8">
            {state === 'failed'
              ? 'Paystack reported the payment as failed. Your order is saved as unpaid — you can retry at any time.'
              : "Paystack hasn't confirmed your payment yet. Your order is saved — you can resume payment from your profile."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={orderId ? `/orders/${orderId}` : '/profile'} data-testid="resume-payment-link">
              <Button variant="ctaPrimary" className="rounded-full px-8 py-6 font-body text-sm font-semibold">
                Retry Payment <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/profile">
              <Button variant="ctaSecondary" className="rounded-full px-8 py-6 font-body text-sm">
                Go to Profile
              </Button>
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
